import { useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile } from '@/types/profile';
import { logger } from '@/utils/logger';

// Centralized profile management with request deduplication
export const useProfileManager = () => {
  const pendingFetchesRef = useRef<Map<string, Promise<UserProfile | null>>>(new Map());
  const profileCacheRef = useRef<Map<string, { profile: UserProfile; timestamp: number }>>(new Map());
  
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Cache profile with timestamp
  const cacheProfile = useCallback((userId: string, profile: UserProfile): void => {
    try {
      profileCacheRef.current.set(userId, {
        profile,
        timestamp: Date.now()
      });
      sessionStorage.setItem(`profile-${userId}`, JSON.stringify(profile));
    } catch (error) {
      logger.error('Error caching profile:', error);
    }
  }, []);

  // Get cached profile with expiration check
  const getCachedProfile = useCallback((userId: string): UserProfile | null => {
    const cached = profileCacheRef.current.get(userId);
    if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
      return cached.profile;
    }

    try {
      const sessionCached = sessionStorage.getItem(`profile-${userId}`);
      if (sessionCached) {
        const profile = JSON.parse(sessionCached) as UserProfile;
        profileCacheRef.current.set(userId, {
          profile,
          timestamp: Date.now()
        });
        return profile;
      }
    } catch (error) {
      logger.error('Error getting cached profile:', error);
    }

    return null;
  }, []);

  // Create default profile for new users
  const createDefaultProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    try {
      logger.debug("Creating default profile for user:", userId);
      
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email || "";
      const username = email ? email.split('@')[0] : `user_${Date.now()}`;
      
      const { data, error } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          username: username,
          display_name: username,
          bio: null,
          avatar_url: null,
          knitting_style: "not-specified",
          measurement_system: "metric",
          email_marketing: true,
          email_project_updates: true,
          email_community_updates: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        logger.error('Error creating default profile:', error);
        return null;
      }

      if (data) {
        const profile = data as UserProfile;
        cacheProfile(userId, profile);
        return profile;
      }
      
      return null;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      logger.error('Error creating default profile:', message);
      return null;
    }
  }, [cacheProfile]);

  // Deduplicated profile fetching
  const fetchProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    // Check for ongoing request
    const pendingFetch = pendingFetchesRef.current.get(userId);
    if (pendingFetch) {
      return pendingFetch;
    }

    // Check cache first
    const cachedProfile = getCachedProfile(userId);
    if (cachedProfile) {
      return cachedProfile;
    }

    // Create new fetch promise
    const fetchPromise = (async (): Promise<UserProfile | null> => {
      try {
        logger.debug("Fetching fresh profile for user:", userId);
        
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) {
          logger.error('Error fetching profile:', error.message);
          
          if (error.code === 'PGRST116') {
            logger.debug("Profile doesn't exist, creating default profile");
            return await createDefaultProfile(userId);
          }
          
          return null;
        }

        if (data) {
          const profile = data as UserProfile;
          cacheProfile(userId, profile);
          return profile;
        }
        
        return null;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        logger.error('Error in fetchProfile:', message);
        return null;
      } finally {
        // Remove from pending requests
        pendingFetchesRef.current.delete(userId);
      }
    })();

    // Store pending request
    pendingFetchesRef.current.set(userId, fetchPromise);
    
    return fetchPromise;
  }, [getCachedProfile, cacheProfile, createDefaultProfile]);

  // Clear cache for user
  const clearProfileCache = useCallback((userId: string): void => {
    profileCacheRef.current.delete(userId);
    try {
      sessionStorage.removeItem(`profile-${userId}`);
    } catch (error) {
      logger.error('Error clearing profile cache:', error);
    }
  }, []);

  return {
    fetchProfile,
    getCachedProfile,
    cacheProfile,
    clearProfileCache
  };
};
